import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/site/Reveal";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", dob: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const field = "w-full bg-transparent border-b border-brand-ink/20 py-4 text-lg outline-none focus:border-brand-orange transition-colors placeholder:text-brand-ink/30";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.dob) {
      toast.error("Email and birthday are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers/signup", form);
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-44 min-h-screen">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-16 items-center pb-28">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-orange mb-6">/ Guest List</div>
          <h1 className="font-head font-extrabold uppercase leading-[0.82] tracking-tighter text-[15vw] md:text-[8vw]">
            <MaskedLines lines={["Get on", <>the <span className="text-brand-orange">list</span></>]} />
          </h1>
          <Reveal delay={0.4} className="mt-8 max-w-md text-brand-ink/60 text-lg">
            First dibs on tickets, invite-only shows, and a little something on your birthday.
            No spam — just the good stuff.
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          {done ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="border border-brand-orange p-10 text-center"
              data-testid="signup-success"
            >
              <div className="w-16 h-16 rounded-full bg-brand-orange mx-auto flex items-center justify-center">
                <Check size={28} className="text-white" />
              </div>
              <h3 className="font-head font-extrabold text-3xl uppercase mt-6">You're in.</h3>
              <p className="text-brand-ink/60 mt-2">Welcome to the family. Watch your inbox.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-brand-ink/10 p-8 md:p-10 space-y-7" data-testid="signup-form">
              <input className={field} placeholder="Your name (optional)" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" />
              <input className={field} type="email" placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/40">Date of birth</label>
                <input className={field} type="date" value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="signup-dob" />
              </div>
              <button type="submit" disabled={loading} data-testid="signup-submit"
                className="w-full rounded-full bg-brand-orange text-white py-4 font-mono text-sm uppercase tracking-[0.2em] hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Join the guest list →"}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
